/* Copyright (c) 2020-present CloudIO Inc */

import { Platform } from '../../platform/Platform';
import { baseComponentManager } from '../../registry/Components';
import { AliasType, ComponentManagerFn } from '../../registry/Component.type';
import { ItemType } from '../../registry/ComponentRegistry';
import { defaultValue } from './FusionMaps';
import { toOptions } from 'componentregistry/utils/ManagerUtils';

const FusionMapsManager: ComponentManagerFn<'FusionMaps'> = (
  platform: Platform,
) => {
  return {
    ...baseComponentManager<'FusionMaps'>(platform, 'FusionMaps'),
    type: 'FusionMaps',
    name: 'Fusion Maps',
    isChildAllowed: (page, item, childType) =>
      (['TriggerOnClick'] as ItemType[]).includes(childType),
    defaultValue,
    props: {
      dsalias: {
        category: 'Data',
        label: 'DataSource Alias',
        required: true,
        type: 'DataSourceAliasChooser',
        allowedTypes: [
          'CloudDataStore',
          'DataStore',
          'LocalDataStore',
          'ReadOnlyDataStore',
        ],
      },
      keyAttribute: {
        aliasType: 'LOV',
        category: 'Data',
        getAttributeDataTypes: () => 'ALL',
        label: 'Key Attribute',
        required: true,
        type: 'ComboAttributeChooser',
        helpText: 'It is the primary key field in the current data source.',
      },
      latitudeAttribute: {
        aliasType: 'LOV',
        category: 'Data',
        getAttributeDataTypes: () => {
          return ['Integer', 'Decimal', 'Double', 'Percent'];
        },
        label: 'Latitude Attribute',
        required: true,
        type: 'ComboAttributeChooser',
        helpText:
          'Specify the field that represents the latitude coordinate for the data points.',
      },
      longitudeAttribute: {
        aliasType: 'LOV',
        category: 'Data',
        getAttributeDataTypes: () => {
          return ['Integer', 'Decimal', 'Double', 'Percent'];
        },
        label: 'Longitude Attribute',
        required: true,
        type: 'ComboAttributeChooser',
        helpText:
          'Specify the field that represents the longitude coordinate for the data points.',
      },
      valueAttribute: {
        aliasType: 'LOV',
        category: 'Data',
        getAttributeDataTypes: () => 'ALL',
        label: 'Value Attribute',
        type: 'ComboAttributeChooser',
        helpText:
          'Choose the field that contains the value information for the data points.',
      },
      heatMapGroupAttribute: {
        aliasType: 'LOV',
        category: 'Data',
        getAttributeDataTypes: () => 'ALL',
        label: 'HeatMap Group Attribute',
        type: 'ComboAttributeChooser',
        helpText:
          'Select the attribute that contains state or region codes. This field will be used to group records and generate the heat map',
      },
      doNotPrefetch: {
        label: 'Do Not Prefetch',
        type: 'boolean',
        category: 'Data',
      },
      caption: {
        label: 'Caption',
        type: 'expression',
        returnType: async () => 'string',
        helpText: 'Provide a title for the Chart.',
      },
      subCaption: {
        label: 'Sub-Caption',
        type: 'expression',
        returnType: async () => 'string',
        helpText: 'Provide a sub title for the chart.',
      },
      chartHeight: {
        label: 'Chart Height',
        type: 'number',
        max: 800,
        min: 0,
        step: 40,
        helpText: 'Provide a number to set height of the chart.',
      },
      chartWidth: {
        label: 'Chart Width',
        type: 'number',
        max: 1000,
        min: 0,
        step: 40,
        helpText: 'Provide width for the chart.',
      },
      markerType: {
        label: 'Marker Type',
        options: toOptions(['circle', 'pin']),
        type: 'select',
        helpText: 'Select a type of marker for the data points.',
      },
      threshold: {
        label: 'Marker Threshold',
        type: 'number',
        max: 5,
        min: 0,
        step: 0.5,
        helpText:
          'Provide a value to set the distance threshold for displaying nearby markers.',
      },
      markerRadius: {
        label: 'Marker Size',
        type: 'number',
        max: 10,
        min: 0,
        step: 1,
        helpText: 'Provide a value to set the radius size of the markers.',
      },
      stateColorsFunction: {
        label: 'State Colors Function',
        type: 'controllerFunction',
        category: 'Look and Feel',
        alwaysShow: true,
        helpText: `
A page controller function to assign colors and custom labels to states on the map.
The function should return an array of objects, where each object contains the following properties:  

- **id**: The state's identifier (e.g., 'WA' for Washington).  
- **color**: The color assigned to the state on the map.  
- **customData**: A custom value for the state. Can be accessed by On Click Trigger.  

###### Example Usage:
\`\`\`javascript
const colorData = () => [
  { id: 'WA', color: '#2E3B55', customData: 'CustomText' },
  { id: 'OR', color: '#2E3B55', customData: 'CustomText' },
];
export default { colorData };`,
      },
      backgroundColor: {
        label: 'Background Color',
        type: 'string',
        category: 'Look and Feel',
      },
      borderColor: {
        label: 'Border Color',
        type: 'string',
        category: 'Look and Feel',
      },
      nullEntityColor: {
        label: 'Entity Color',
        type: 'string',
        category: 'Look and Feel',
      },
      markerColor: {
        label: 'Marker Color',
        type: 'string',
        category: 'Look and Feel',
      },
      labelColor: {
        label: 'Entity Label Color',
        type: 'string',
        category: 'Look and Feel',
      },
      baseFontColor: {
        label: 'Base Font Color',
        type: 'string',
        category: 'Look and Feel',
      },
      showLabels: {
        label: 'Show Labels',
        type: 'boolean',
        helpText: 'Enable this to display labels of the chart.',
      },
      showMarkerLabels: {
        label: 'Show Marker Labels',
        type: 'boolean',
        helpText: 'Enable this to display labels of the markers.',
      },
      showLegend: {
        label: 'Show Legend',
        type: 'boolean',
        helpText: 'Enable this to display Legend.',
      },
      legendPosition: {
        label: 'Legend Position',
        options: toOptions(['bottom', 'right']),
        type: 'select',
        helpText: 'Select a position of the Legend.',
      },
      legendFunction: {
        label: 'Legend Names Function',
        type: 'controllerFunction',
        alwaysShow: true,
        helpText: `
A page controller function to assign legend names and colors for the map.
The function returns an array of objects, where each object contains the following properties:  

- **name**: The legend name.  
- **color**: The color assigned to the corresponding region on the map.  

###### Example Usage:
\`\`\`javascript
const regions = () => [
  { name: 'West', color: '#2E3B55' },
  { name: 'Southwest', color: '#A45C40' },
  { name: 'South', color: '#2D132C' },
];
export default { regions };`,
      },
      heatMap: {
        label: 'Enable Heat Map',
        type: 'boolean',
        helpText: 'Enable this to show HeatMap',
      },
      showHeatMapLegend: {
        label: 'Show Heat Map Legend',
        type: 'boolean',
        helpText: 'Enable this to show HeatMap Legend',
      },
      heatMapLegendPosition: {
        label: 'HeatMap Legend Position',
        options: toOptions(['bottom', 'right']),
        type: 'select',
        helpText: 'Select a position of the Legend.',
      },
      heatMapLegendType: {
        label: 'HeatMap Legend Type',
        options: toOptions(['gradient', 'steps']),
        type: 'select',
        helpText: 'Select a position of the Legend.',
      },
      heatMapColorRangeFunction: {
        label: 'Heat Map Color Range',
        type: 'controllerFunction',
        alwaysShow: true,
        helpText: `
 A page controller function to define heat map color ranges.  
      The function should return an array of objects, where each object contains:
      
- **minvalue**: Minimum value of the color range.  
- **maxvalue**: Maximum value of the color range.  
- **color**: Hex color code for this range.  

  ###### Example Usage:
\`\`\`javascript
   const colorRange = [
        { minvalue: 1,  maxvalue: 30, code: '#FFCC80' },
        { minvalue: 31, maxvalue: 60, code: '#FFB74D' },
        { minvalue: 61, maxvalue: 100, code: '#FF5722' },
      ];
      
      export default { colorRange };`,
      },
    },
    isPropertyVisible(prop, itemData): boolean {
      switch (prop) {
        case 'keyAttribute':
        case 'latitudeAttribute':
        case 'longitudeAttribute':
        case 'valueAttribute':
          return !!itemData.dsalias;
        case 'labelColor':
          return itemData.showLabels;
        case 'legendPosition':
        case 'legendFunction':
          return itemData.showLegend;
        case 'stateColorsFunction':
          return !itemData.heatMap;
        case 'markerColor':
        case 'markerRadius':
          return itemData.markerType === 'circle';
        case 'heatMapColorRangeFunction':
        case 'showHeatMapLegend':
        case 'heatMapGroupAttribute':
          return !!itemData.heatMap;
        case 'heatMapLegendPosition':
        case 'heatMapLegendType':
          return !!itemData.heatMap && !!itemData.showHeatMapLegend;
        default:
          return true;
      }
    },
    getDataSourceAliasProperty: (aliasType: AliasType) =>
      aliasType === 'LOV'
        ? { aliasProp: 'dsalias', embeddedPageIdProp: 'embeddedPageId' } // TODO: Add embeddedPageId prop
        : undefined,
    isDataSourceAliasUsed: (item, alias) => item.props.dsalias === alias,
    category: 'Forms',
    icon: { iconName: 'map-location-dot' },
    description: `This is an interactive map component that visualizes locations across the United States. It includes features such as customizable markers, tooltips for displaying detailed information, and configurable thresholds for highlighting nearby markers.  
Additionally, the map supports a **"trigger-on-click"** event, allowing access to **marker data** when a marker is clicked and **state data** when a state is clicked.  

###### Marker Click Event Properties:
- \`$._.event?.id\` → Passes the **Key Attribute** specified in the UI.  
- \`$._.event?.value\` → Passes the **Value Attribute** specified in the UI.  
- \`$._.event?.latitude\` → Passes the **Latitude Attribute** specified in the UI.  
- \`$._.event?.longitude\` → Passes the **Longitude Attribute** specified in the UI.  

###### State Click Event Properties:
- \`$._.event?.id\` → Passes the **ID of the State Clicked**.  
- \`$._.event?.value\` → Passes the **Label of the State Clicked**.  
- \`$._.event?.customData\` → Passes the **Custom Text** specified in the **State Color Controller Function**.  

###### Legend Click Event Properties:
- \`$._.event?.label\` → Passes the **Label of the Legend Clicked**.  
- \`$._.event?.color\` → Passes the **Color of the Legend Clicked**.  
    `,
  };
};

export default FusionMapsManager;
